import { SquareTabContainer, TitledChild } from "Components";
import { PositionedPopup } from "Components/Shared/PositionedPopup";
import { SynonymControllerSingleton } from "Controllers";
import { SynonymTypeEnum } from "Enums";
import { FC, useCallback, useEffect, useState, CSSProperties } from "react";
import { AddableSynonym } from "./AddableSynonym";

import styles from "./generatedSynonymsPopup.module.scss";

// TODO: Maybe do something about the props waterfall
type TGeneratedSynonymsPopupProps = {
    parentElementText: string,
    synonymType: SynonymTypeEnum,
    parentElementId: string,
    isHistoricalEnabled?: boolean,
    addSynonym: (synonymText: string) => Promise<void>,
    changePopupOpen: (isOpen: boolean) => void,
    extraStyle?: CSSProperties,
}

export const GeneratedSynonymsPopup: FC<TGeneratedSynonymsPopupProps> = ({ parentElementText, synonymType, parentElementId,
        isHistoricalEnabled, addSynonym, changePopupOpen, extraStyle }: TGeneratedSynonymsPopupProps) => {

    // State
    const [generatedSynonyms, setGeneratedSynonyms] = useState<string[] | undefined>(undefined);
    const [historicalSynonyms, setHistoricalSynonyms] = useState<string[] | undefined>(undefined);

    // Define the title of the generated synonyms tab 
    const generatedTitle = isHistoricalEnabled ? "Generated" : "Generated synonyms";

    const retrieveGeneratedSynonyms = useCallback(async () => {
        const generatedSynonyms = await SynonymControllerSingleton.retrieveGeneratedSynonyms(parentElementText, synonymType, parentElementId);
        setGeneratedSynonyms(generatedSynonyms);
    }, [parentElementId, parentElementText, synonymType]);

    const retrieveHistoricalSynonyms = useCallback(async () => {
        const historicalSynonyms = await SynonymControllerSingleton.retrieveHistoricalSynonyms(parentElementText, synonymType, parentElementId);
        setHistoricalSynonyms(historicalSynonyms);
    }, [parentElementId, parentElementText, synonymType]);

    const onAddSynonym = (synonymText: string) => {
        addSynonym(synonymText);
        if(generatedSynonyms) {
            setGeneratedSynonyms(generatedSynonyms.filter((synonym) => { return synonym !== synonymText; }));
        }
        if(historicalSynonyms) {
            setHistoricalSynonyms(historicalSynonyms.filter((synonym) => { return synonym !== synonymText; }));
        }
    };

    useEffect(() => {
        retrieveGeneratedSynonyms();
        if (isHistoricalEnabled) {
            retrieveHistoricalSynonyms();
        }
    }, [isHistoricalEnabled, parentElementId, parentElementText, synonymType, retrieveGeneratedSynonyms, retrieveHistoricalSynonyms]);

    return (
        <PositionedPopup extraClassName={styles.popup} onClickOutside={() => { changePopupOpen(false); }} extraStyle={extraStyle}>
            <SquareTabContainer extraClassNameTabContainer={styles.tabContainer} extraClassNameTabContentContainer={styles.tabContainerContent}
                extraClassNameTab={styles.tab} extraClassNameActiveTab={styles.activeTitle} >
                <TitledChild title={generatedTitle}>
                    {generatedSynonyms ?
                        generatedSynonyms.map((synonym) => {
                            return (
                                <AddableSynonym key={`generated${synonym}`} synonym={synonym} onAdd={onAddSynonym} />
                            );
                        })
                        :
                        <span className={styles.noResults}>No synonyms found</span>
                    }
                </TitledChild>

                {isHistoricalEnabled ?
                    <TitledChild title="Historical">
                        {historicalSynonyms && historicalSynonyms.length > 0 ?
                            historicalSynonyms.map((synonym) => {
                                return (
                                    <AddableSynonym key={`historical${synonym}`} synonym={synonym} onAdd={onAddSynonym} />
                                );
                            })
                            :
                            <span className={styles.noResults}>No synonyms found</span>
                        }
                    </TitledChild>
                    : null}
            </SquareTabContainer>
        </PositionedPopup>
    );
};